import { useEffect, useRef, useState } from 'react';
import { Plus, Download, Layers } from 'lucide-react';
import Layout from '../components/Layout';
import PaymentModal from '../components/PaymentModal';
import BulkPaymentsModal from '../components/BulkPaymentsModal';
import { useAuth } from '../hooks/useAuth';
import { api, formatCurrency, API_BASE } from '../lib/api';

const MultiSelectDropdown = ({ options, selected, onChange, placeholder = 'All', testId }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v]);
  const label = selected.length === 0 ? placeholder : selected.length === 1 ? selected[0] : `${selected.length} selected`;
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen(o => !o)}
        className="w-full bg-transparent border border-border/60 focus:border-maroon outline-none px-2 py-1 font-body text-xs text-left flex items-center justify-between"
      >
        <span className={selected.length === 0 ? 'text-text-secondary' : 'text-text-primary'}>{label}</span>
        <span className="ml-1 text-text-secondary">▾</span>
      </button>
      {open && (
        <div className="absolute z-20 mt-1 left-0 min-w-full bg-cream border border-border shadow-md">
          {options.map(opt => (
            <label
              key={opt}
              data-testid={`${testId}-opt-${opt.replace(/\s/g, '-')}`}
              className={`flex items-center gap-2 px-3 py-2 text-xs font-body cursor-pointer whitespace-nowrap hover:bg-surface ${selected.includes(opt) ? 'text-maroon' : 'text-text-primary'}`}
            >
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={() => toggle(opt)}
                className="accent-maroon"
              />
              {opt}
            </label>
          ))}
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="w-full border-t border-border px-3 py-2 text-[10px] uppercase tracking-widest text-text-secondary hover:text-maroon"
            >
              Clear selection
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  // Per-column filters (client-side)
  const [colFilter, setColFilter] = useState({ invoice: '', client: '', bank: '', mode: '', payment_types: [], reference: '' });
  const [amtMin, setAmtMin] = useState('');
  const [amtMax, setAmtMax] = useState('');
  const { isViewer } = useAuth();

  const fetchPayments = () => {
    const params = {};
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    api.get('/payments', { params }).then(res => setPayments(res.data));
  };

  useEffect(() => {
    fetchPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  const filteredPayments = payments.filter(p => {
    if (colFilter.invoice && !(p.invoice_no || '').toLowerCase().includes(colFilter.invoice.toLowerCase())) return false;
    if (colFilter.client && !(p.client_name || '').toLowerCase().includes(colFilter.client.toLowerCase())) return false;
    if (colFilter.bank && !(p.bank_label || '').toLowerCase().includes(colFilter.bank.toLowerCase())) return false;
    if (colFilter.mode && !(p.mode || '').toLowerCase().includes(colFilter.mode.toLowerCase())) return false;
    if (colFilter.payment_types.length > 0 && !colFilter.payment_types.includes(p.payment_type || '')) return false;
    if (colFilter.reference && !(p.reference || '').toLowerCase().includes(colFilter.reference.toLowerCase())) return false;
    const amt = parseFloat(p.amount) || 0;
    if (amtMin && amt < parseFloat(amtMin)) return false;
    if (amtMax && amt > parseFloat(amtMax)) return false;
    return true;
  });
  const filteredTotal = filteredPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const hasColFilters = colFilter.invoice || colFilter.client || colFilter.bank || colFilter.mode || colFilter.reference || colFilter.payment_types.length > 0 || amtMin || amtMax;
  const clearColFilters = () => { setColFilter({ invoice: '', client: '', bank: '', mode: '', payment_types: [], reference: '' }); setAmtMin(''); setAmtMax(''); };

  const inputCls = "bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm transition-colors";

  return (
    <Layout
      title="Payments"
      action={
        <div className="flex gap-2">
          <button data-testid="export-payments-csv" onClick={() => {
            const q = new URLSearchParams();
            if (fromDate) q.set('start_date', fromDate);
            if (toDate) q.set('end_date', toDate);
            window.open(`${API_BASE}/exports/payments.csv?${q.toString()}`, '_blank');
          }} className="bg-transparent text-text-secondary border border-border hover:border-maroon hover:text-maroon px-4 py-2 font-heading text-sm flex items-center transition-colors">
            <Download className="w-4 h-4 mr-2" strokeWidth={1.5} /> Export CSV
          </button>
          {!isViewer && (
            <>
              <button data-testid="bulk-add-payments-button" onClick={() => setShowBulk(true)} className="bg-transparent text-maroon border border-maroon hover:bg-cream px-4 py-2 font-heading text-sm flex items-center transition-colors">
                <Layers className="w-4 h-4 mr-2" strokeWidth={1.5} /> Bulk Add
              </button>
              <button data-testid="record-payment-modal-button" onClick={() => setShowModal(true)} className="bg-maroon text-white hover:bg-maroon-hover px-6 py-3 font-heading text-base flex items-center">
                <Plus className="w-5 h-5 mr-2" strokeWidth={1} /> Record Payment
              </button>
            </>
          )}
        </div>
      }
    >
      <div className="bg-surface border border-border p-6">
        <div className="flex gap-4 mb-6 items-end flex-wrap">
          <div>
            <label className="block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">From Date</label>
            <input data-testid="payments-from-date" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2">To Date</label>
            <input data-testid="payments-to-date" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className={inputCls} />
          </div>
          {(fromDate || toDate) && (
            <button onClick={() => { setFromDate(''); setToDate(''); }} className="font-body text-xs uppercase tracking-widest text-maroon hover:text-maroon-hover py-2">Clear dates</button>
          )}
          {hasColFilters && (
            <button data-testid="clear-col-filters" onClick={clearColFilters} className="font-body text-xs uppercase tracking-widest text-maroon hover:text-maroon-hover py-2">Clear filters</button>
          )}
          <div className="ml-auto text-right">
            <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-1">{hasColFilters ? 'Filtered Total' : ((fromDate || toDate) ? 'Range Total' : 'All-time Total')}</p>
            <p data-testid="payments-range-total" className="font-numbers text-2xl text-success tabular-nums">
              {formatCurrency(filteredTotal)}
            </p>
            <p className="font-body text-xs text-text-secondary">{filteredPayments.length} payment{filteredPayments.length === 1 ? '' : 's'}{hasColFilters && payments.length !== filteredPayments.length ? ` (of ${payments.length})` : ''}</p>
          </div>
        </div>

        <table className="w-full">
          <thead>
            <tr>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Date</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Invoice</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Client</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Bank</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Mode</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Type</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Reference</th>
              <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Amount</th>
            </tr>
            <tr>
              <th className="py-2 pr-2"><span className="text-[10px] uppercase tracking-widest text-text-secondary">—</span></th>
              <th className="py-2 pr-2"><input data-testid="filter-invoice" value={colFilter.invoice} onChange={(e) => setColFilter({ ...colFilter, invoice: e.target.value })} placeholder="Filter invoice…" className="w-full bg-transparent border border-border/60 focus:border-maroon outline-none px-2 py-1 font-body text-xs" /></th>
              <th className="py-2 pr-2"><input data-testid="filter-client" value={colFilter.client} onChange={(e) => setColFilter({ ...colFilter, client: e.target.value })} placeholder="Filter client…" className="w-full bg-transparent border border-border/60 focus:border-maroon outline-none px-2 py-1 font-body text-xs" /></th>
              <th className="py-2 pr-2"><input data-testid="filter-bank" value={colFilter.bank} onChange={(e) => setColFilter({ ...colFilter, bank: e.target.value })} placeholder="Filter bank…" className="w-full bg-transparent border border-border/60 focus:border-maroon outline-none px-2 py-1 font-body text-xs" /></th>
              <th className="py-2 pr-2">
                <select data-testid="filter-mode" value={colFilter.mode} onChange={(e) => setColFilter({ ...colFilter, mode: e.target.value })} className="w-full bg-transparent border border-border/60 focus:border-maroon outline-none px-2 py-1 font-body text-xs">
                  <option value="">All</option>
                  {['NEFT', 'RTGS', 'IMPS', 'UPI', 'Cheque', 'Cash', 'TDS'].map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </th>
              <th className="py-2 pr-2">
                <MultiSelectDropdown
                  testId="filter-type"
                  options={['AD Spend', 'Retainer', 'One-time', 'Subscription', 'TDS']}
                  selected={colFilter.payment_types}
                  onChange={(next) => setColFilter({ ...colFilter, payment_types: next })}
                  placeholder="All"
                />
              </th>
              <th className="py-2 pr-2"><input data-testid="filter-reference" value={colFilter.reference} onChange={(e) => setColFilter({ ...colFilter, reference: e.target.value })} placeholder="Filter ref…" className="w-full bg-transparent border border-border/60 focus:border-maroon outline-none px-2 py-1 font-body text-xs" /></th>
              <th className="py-2">
                <div className="flex gap-1">
                  <input data-testid="filter-amt-min" type="number" value={amtMin} onChange={(e) => setAmtMin(e.target.value)} placeholder="min" className="w-1/2 bg-transparent border border-border/60 focus:border-maroon outline-none px-2 py-1 font-body text-xs text-right" />
                  <input data-testid="filter-amt-max" type="number" value={amtMax} onChange={(e) => setAmtMax(e.target.value)} placeholder="max" className="w-1/2 bg-transparent border border-border/60 focus:border-maroon outline-none px-2 py-1 font-body text-xs text-right" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredPayments.map(p => (
              <tr key={p.id} data-testid={`payment-row-${p.id}`} className="border-b border-border hover:bg-cream transition-colors">
                <td className="py-3 font-body text-sm">{p.date}</td>
                <td className="py-3 font-body text-sm">{p.invoice_no}</td>
                <td className="py-3 font-body text-sm">{p.client_name}</td>
                <td className="py-3 font-body text-sm text-text-secondary">{p.bank_label}</td>
                <td className="py-3 font-body text-sm">{p.mode}</td>
                <td className="py-3 font-body text-sm text-text-secondary">{p.payment_type || '—'}</td>
                <td className="py-3 font-body text-sm text-text-secondary">{p.reference || '—'}</td>
                <td className="py-3 font-numbers text-sm text-success text-right tabular-nums">{formatCurrency(p.amount)}</td>
              </tr>
            ))}
            {filteredPayments.length === 0 && <tr><td colSpan="8" className="py-8 text-center text-text-secondary font-body text-sm">No payments match filters</td></tr>}
          </tbody>
        </table>
      </div>

      {showModal && (
        <PaymentModal
          onClose={() => setShowModal(false)}
          onSaved={() => { setShowModal(false); fetchPayments(); }}
        />
      )}
      {showBulk && (
        <BulkPaymentsModal
          onClose={() => setShowBulk(false)}
          onSaved={() => { setShowBulk(false); fetchPayments(); }}
        />
      )}
    </Layout>
  );
};

export default Payments;
